import React, { useState, useRef, useEffect } from 'react';
import {
  QuestionMarkCircleIcon,
  ClipboardIcon,
  MailIcon,
  PhoneIcon,
  UserCircleIcon,
  LogoutIcon,
} from '@heroicons/react/solid';
import PlantStatus from './PlantStatus';
import { useLocation } from 'react-router-dom';

const titles = {
  '/live': 'Live Dashboard',
  '/kpi': 'Metallurgical & KPI',
  '/production': 'Month To Date',
  '/lab': 'Lab Inspections',
  '/process-checks': 'Process Equipment Checks',
  '/setup': 'Account Setup',
};

function Header() {
  const location = useLocation();
  const title = titles[location.pathname] || '';
  const [showSupport, setShowSupport] = useState(false);
  const [user, setUser] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const supportRef = useRef(null);
  const menuRef = useRef(null);

  // Close panel when clicking outside
  useEffect(() => {
    const handler = e => {
      if (supportRef.current && !supportRef.current.contains(e.target)) {
        setShowSupport(false);
      }
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Close panel on Escape key
  useEffect(() => {
    const escHandler = e => {
      if (e.key === 'Escape') {
        setShowSupport(false);
        setShowMenu(false);
      }
    };
    document.addEventListener('keydown', escHandler);
    return () => document.removeEventListener('keydown', escHandler);
  }, []);

  useEffect(() => {
    fetch('/api/user.php?action=get_profile')
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(data => setUser(data.user))
      .catch(() => {
        localStorage.removeItem('loggedIn');
        sessionStorage.removeItem('loggedIn');
        window.location.href = 'login.html';
      });
  }, []);

  const logout = () => {
    fetch('/api/user.php?action=logout')
      .finally(() => {
        localStorage.removeItem('loggedIn');
        sessionStorage.removeItem('loggedIn');
        window.location.href = 'login.html';
      });
  };

  return (
    <header className="bg-[#0159B2] relative flex items-center px-6 py-2 text-white rounded-full w-full">
      <div className="flex items-center flex-1">
        <PlantStatus />
      </div>
      <span className="font-semibold text-center">{title}</span>
      <div className="flex items-center flex-1 justify-end">
        {/* Support button */}
        <button
          onClick={e => {
            e.stopPropagation();
            setShowSupport(s => !s);
          }}
          className="mr-4 flex items-center space-x-1 px-3 py-1 rounded-full border border-white hover:bg-white hover:text-[#0159B2] transition"
        >
          <QuestionMarkCircleIcon className="h-5 w-5" />
          <span className="text-sm">Support</span>
        </button>
        <a
          href="https://forms.bultfontein.enprotec.com"
          target="_blank"
          rel="noopener noreferrer"
          className="mr-4 flex items-center space-x-1 bg-[#00D04C] hover:bg-green-600 px-3 py-1 rounded-full text-white transition"
        >
          <ClipboardIcon className="h-5 w-5" />
          <span className="text-sm">Forms</span>
        </a>
        {/* User Menu */}
        {user && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={e => {
                e.stopPropagation();
                setShowMenu(s => !s);
              }}
              className="focus:outline-none rounded-full overflow-hidden border-2 border-white"
            >
              {user.profile_picture_url ? (
                <img
                  src={user.profile_picture_url}
                  alt="avatar"
                  className="w-8 h-8 object-cover"
                />
              ) : (
                <UserCircleIcon className="h-8 w-8 text-white" />
              )}
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white text-gray-800 rounded-md shadow-xl z-50">
                <div className="p-4 border-b">
                  <div className="font-semibold">{user.full_name || user.username}</div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                  <div className="text-xs mt-1">
                    {Number(user.is_admin) === 1 ? 'Admin' : 'User'}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowMenu(false);
                    window.location.href = '/setup';
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100"
                >
                  Profile
                </button>
                {Number(user.is_admin) === 1 && (
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      window.location.href = '/setup?tab=users';
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    Manage Users
                  </button>
                )}
                <button
                  onClick={logout}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                >
                  <LogoutIcon className="h-4 w-4" /> Logout
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Support Panel with animation */}
      {showSupport && (
        <div
          ref={supportRef}
          onClick={e => e.stopPropagation()}
          className="absolute right-4 top-full mt-2 bg-white text-gray-800 rounded-lg shadow-2xl w-80 p-4 z-[9999] animate-slide-down"
        >
          <ul className="space-y-3 text-sm">
            <li className="flex items-center space-x-2">
              <MailIcon className="h-5 w-5 text-blue-500" />
              <a
                href="mailto:service@digirockinnovations.com"
                className="hover:underline break-all"
              >
                service@digirockinnovations.com
              </a>
            </li>
            <li className="flex items-center space-x-2">
              <PhoneIcon className="h-5 w-5 text-green-600" />
              <a href="tel:0768842212" className="hover:underline">
                060&nbsp;988&nbsp;6599
              </a>
            </li>
            <li className="flex items-center space-x-2">
              <PhoneIcon className="h-5 w-5 text-green-600" />
              <a href="tel:0725620177" className="hover:underline">
                072&nbsp;562&nbsp;0177
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}

export default Header;
